import React, { useState, useEffect, useCallback } from 'react';
import { Col, Container, Row, Form } from 'react-bootstrap';
import Helmet from 'react-helmet';
import { sortBy } from 'lodash';

import { ApiServiceServerless, isCancel, useCancelToken } from '../xhr_libs';
import AddBuildingModal from '../components/modals/BuildingModals/AddBuildingModal';
import OrganizationModal from '../components/modals/OrganizationModal';
import EnergyTracerTable, {
  AddEntityFilterHeader,
  AddEntityFilterHeaderPreference,
} from '../components/tables';
import PageHeader from '../components/PageHeader';
import Loader from '../components/Loader';

import OrganizationsFromPortfolios from '../helpers/OrganizationsFromPortfolios';
import { linkFormatter } from '../components/tables/formatters';
import {
  organizationBuildingFormatter,
  organizationBuildingFilter,
} from '../components/tables/formatters/organizationTable';
import { textFilterIncludesPreference } from '../components/tables/filters';
import { useOrganizationRolesFilter } from '../helpers/checkRoles';
import { getUserPreference } from '../helpers/user-preferences';

const PAGE_TITLE = 'Organization Management';

const getTableColumns = (
  showOrganizationModal,
  showBuildingModal,
  buildingCreateAccess,
  buildingFilterValue,
  setBuildingFilterValue,
  expandBuildingRows
) => [
  {
    dataField: 'name',
    text: 'Organization',
    formatter: linkFormatter((row) => [`/organizations/${row.id}`, row.name]),
    filter: textFilterIncludesPreference(
      'Organization',
      'name',
      undefined,
      true,
      'last_filtered_org'
    ),
    filterRenderer: (onFilter) => (
      <AddEntityFilterHeaderPreference
        onFilter={onFilter}
        filterText='Filter organizations'
        buttonText='New Organization'
        onButtonClick={showOrganizationModal}
        showButton={true}
        defaultValue={getUserPreference('last_filtered_org')}
      />
    ),
  },
  {
    dataField: 'location',
    text: 'Location',
    formatter: (cell, row) =>
      row.location[1]
        ? `${row.location[1]}, ${row.location[0]}`
        : `${row.location[0]}`,
  },
  {
    dataField: 'buildings',
    text: 'Buildings',
    formatExtraData: {
      buildingFilterValue: buildingFilterValue,
      expandBuildingRows: expandBuildingRows,
    },
    formatter: (cell, row, rowIndex, formatExtraData) =>
      organizationBuildingFormatter(row, formatExtraData),
    filter: organizationBuildingFilter(setBuildingFilterValue),
    filterRenderer: (onFilter) => (
      <>
        <AddEntityFilterHeader
          onFilter={onFilter}
          filterText='Filter buildings'
          buttonText='New Building'
          onButtonClick={showBuildingModal}
          showButton={buildingCreateAccess}
        />
      </>
    ),
  },
];

function Organizations(props) {
  const { showToast, userOrganizations, userSelectedPortfolio, history } =
    props;

  const [authOrganizations, setAuthOrganizations] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const [showOrganizationModal, setShowOrganizationModal] = useState(false);
  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [defaultOrg, setDefaultOrg] = useState(null);

  const [buildingFilterValue, setBuildingFilterValue] = useState('');
  const [expandBuildingRows, setExpandBuildingRows] = useState(false);

  const { cancelToken, cancelled } = useCancelToken();

  const buildingCreateAccess = useOrganizationRolesFilter(
    authOrganizations,
    'building',
    'create'
  );

  const tableColumns = getTableColumns(
    () => setShowOrganizationModal(true),
    () => setShowBuildingModal(true),
    buildingCreateAccess.length > 0,
    buildingFilterValue,
    setBuildingFilterValue,
    expandBuildingRows
  );

  const getAuthOrganizations = useCallback(
    (portfolio_id) => {
      setAuthOrganizations(
        OrganizationsFromPortfolios(userOrganizations, portfolio_id)
      );
    },
    [userOrganizations]
  );

  useEffect(() => {
    getAuthOrganizations(userSelectedPortfolio.id);
  }, [userSelectedPortfolio.id, getAuthOrganizations]);

  const getOrganizations = useCallback(
    (portfolio_id) => {
      setIsLoading(true);
      let url = '/organizations';
      if (parseInt(portfolio_id) !== -1)
        url = `${url}?portfolio_id=${portfolio_id}`;
      ApiServiceServerless.get(url, {
        cancelToken: cancelToken,
      })
        .then((res) => {
          setOrganizations(sortBy(res.data, ['name']));
          setIsLoading(false);
        })
        .catch((err) => {
          if (isCancel(err)) return;
          throw err;
        });
    },
    [cancelToken]
  );

  useEffect(() => {
    if (cancelled) return;
    getOrganizations(userSelectedPortfolio.id);
  }, [userSelectedPortfolio.id, getOrganizations, cancelled]);

  const handleSetNewOrganization = (data) => {
    setOrganizations((prev) => [...prev, data]);
  };

  const handleSetNewBuilding = (data) => {
    setOrganizations((prev) =>
      prev.map((org) => {
        if (org['id'] !== data.organization.id) return org;
        let ret = org;
        org.buildings = [...org.buildings, data];
        return ret;
      })
    );
  };

  return (
    <Container className='et-container'>
      <Helmet>
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <PageHeader
        title={PAGE_TITLE}
        showPortfolioSelect={true}
        isLoading={isLoading}
      />
      <Row style={{ marginTop: '-1em' }}>
        <Col xs={12}>
          {isLoading ? (
            <Loader style={{ marginTop: '8vh' }} />
          ) : (
            <div>
              <Form style={{ textAlign: 'right' }}>
                <input
                  type='radio'
                  checked={expandBuildingRows}
                  onChange={() => {}}
                  onClick={() => {
                    setExpandBuildingRows((prev) => !prev);
                  }}
                  data-id='expand_building_row'
                />
                <label style={{ marginLeft: '0.5em' }}>
                  Expand Building Rows
                </label>
              </Form>
              <EnergyTracerTable
                data={organizations}
                keyField='id'
                columns={tableColumns}
                enableFilter={true}
              />
              {organizations.length === 0 ? (
                <div style={{ width: '100%', border: 'solid 1px' }}>
                  <div
                    style={{
                      width: '80%',
                      paddingTop: '4em',
                      paddingBottom: '4em',
                      marginLeft: '30%',
                    }}
                  >
                    It looks like you do not have any organizations yet.
                    <br></br> Start by creating an organization, click the green
                    "New Organization" button above.
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          )}
        </Col>
      </Row>

      <OrganizationModal
        show={showOrganizationModal}
        onHide={() => setShowOrganizationModal(false)}
        handleSetOrganization={handleSetNewOrganization}
        setDefaultOrg={setDefaultOrg}
        openBuildingModal={() => setShowBuildingModal(true)}
        showToast={showToast}
        history={history}
      />
      <AddBuildingModal
        organizations={authOrganizations}
        organization={defaultOrg}
        handleSetBuilding={handleSetNewBuilding}
        setDefaultOrg={setDefaultOrg}
        openBuildingModal={() => setShowBuildingModal(true)}
        showToast={showToast}
        show={showBuildingModal}
        addAccountOption={false}
        history={history}
        onHide={() => {
          setShowBuildingModal(false);
          setDefaultOrg(null);
        }}
      />
      <div style={{ marginBottom: '20px' }} />
    </Container>
  );
}

export default Organizations;
